import { isNotDefined } from '@sgme/fp';

import { optionApi } from '@/api/option/index.ts';
import type { ProductType } from '@/api/user/getCurrentUser.ts';
import { workspaceApi } from '@/api/workspace/index.ts';
import { cashApi } from '@/store/cash/api/index.ts';
import { espStreamUnsubscribed } from '@/store/esp/slice.ts';
import { flexiForwardApi } from '@/store/flexi-forward/api/index.ts';
import { getFlexiForwardRfsId } from '@/store/flexi-forward/helpers/rfs.ts';
import { forwardAccumulatorApi } from '@/store/forwardAccumulator/api/index.ts';
import { getForwardAccumulatorRfsId } from '@/store/forwardAccumulator/selectors.ts';
import type { StartAppListening } from '@/store/listenerMiddleware.ts';
import { getRfsId } from '@/store/option/selectors/optionSelectors.ts';
import type { AppDispatch, AppState } from '@/store/store.ts';
import { swapApi } from '@/store/swap/api/index.ts';
import { removeTab } from '@/store/tabs/slice.ts';
import { targetAccumulatorApi } from '@/store/targetAccumulator/api/index.ts';
import { getTargetAccumulatorRfsId } from '@/store/targetAccumulator/selectors.ts';
import { removeTile } from '@/store/tiles/slice';

export const registerWorkspaceListeners = (startAppListening: StartAppListening) => {
  startAppListening({
    matcher: workspaceApi.endpoints.getSharedTabList.matchFulfilled,
    effect: (action, { dispatch }) => {
      action.payload.forEach((tab) => {
        dispatch(workspaceApi.endpoints.getSharedTab.initiate(tab.owner));
      });
    },
  });

  startAppListening({
    actionCreator: removeTab,
    effect: ({ payload: tabId }, { getOriginalState, dispatch }) => {
      const state = getOriginalState();
      const tileIdsToRemove = Object.values(state.tiles.entities)
        .filter((tile) => tile.tabId === tabId)
        .map((tile) => ({ tileId: tile.tileId, productType: tile.productType }));

      tileIdsToRemove.forEach((tile) => {
        cleaners[tile.productType]?.(tile.tileId, state, dispatch);
        dispatch(removeTile(tile.tileId));
      });
    },
  });
};

// removeTab logic
//-------------------------------------------------------------------------

type Cleaner = ((tileId: string, state: AppState, dispatch: AppDispatch) => void) | null;

export const cleaners: Record<ProductType, Cleaner> = {
  TargetAccumulator: cleanupTargetAccumulator,
  ForwardAccumulator: cleanupForwardAccumulator,
  FlexiForward: cleanupFlexiForward,
  Cash: cleanupCash,
  Swap: cleanupSwap,
  Option: cleanupOption,
  TakeProfitOrder: cleanupOrder,
  StopLossOrder: cleanupOrder,
  CallOrder: cleanupOrder,
  FixingOrder: cleanupOrder,
  'Twap+Order': cleanupOrder,
  NightjarOrder: cleanupOrder,
  FalconOrder: cleanupOrder,
  SmartRfs: null,
};

function cleanupTargetAccumulator(tileId: string, state: AppState, dispatch: AppDispatch): void {
  const product = state.targetAccumulator[tileId];
  if (!product) return;

  const rfsId = getTargetAccumulatorRfsId(product);
  if (rfsId) {
    dispatch(targetAccumulatorApi.endpoints.cancelTargetAccumulatorRfs.initiate({ tileId, rfsId }));
  }

  const { currencyPair, tiering } = product.properties;
  if (currencyPair && tiering) {
    dispatch(espStreamUnsubscribed({ tileId, currencyPair, tiering }));
  }
}

function cleanupForwardAccumulator(tileId: string, state: AppState, dispatch: AppDispatch): void {
  const product = state.forwardAccumulator[tileId];
  if (!product) return;

  const rfsId = getForwardAccumulatorRfsId(product);
  if (rfsId) {
    dispatch(forwardAccumulatorApi.endpoints.cancelForwardAccumulatorRfs.initiate({ tileId, rfsId }));
  }

  const { currencyPair, tiering } = product.properties;
  if (currencyPair && tiering) {
    dispatch(espStreamUnsubscribed({ tileId, currencyPair, tiering }));
  }
}

function cleanupFlexiForward(tileId: string, state: AppState, dispatch: AppDispatch): void {
  const product = state.flexiForward[tileId];
  if (!product) return;

  const rfsId = getFlexiForwardRfsId(product.properties.rfs);
  if (rfsId) {
    dispatch(flexiForwardApi.endpoints.cancelFlexiForwardRfs.initiate({ tileId, rfsId, removeQuote: true }));
  }
}

function cleanupCash(tileId: string, state: AppState, dispatch: AppDispatch): void {
  const product = state.cash[tileId];
  if (!product) return;

  const rfsId = product.properties.rfs?.rfsId;
  if (rfsId) {
    dispatch(cashApi.endpoints.cancelCashRfs.initiate({ tileId, rfsId }));
  }

  if (product.type !== 'FxFwd') {
    const { currencyPair, margin } = product.properties;
    if (currencyPair && margin?.isTieringSuccess) {
      dispatch(espStreamUnsubscribed({ tileId, currencyPair, tiering: margin.tiering }));
    }
  }
}

function cleanupSwap(tileId: string, state: AppState, dispatch: AppDispatch): void {
  const product = state.swap[tileId];
  if (!product) return;

  const rfsId = product.properties.rfs?.rfsId;
  if (rfsId) {
    dispatch(swapApi.endpoints.cancelSwapRfs.initiate({ tileId, rfsId }));
  }
}

function cleanupOption(tileId: string, state: AppState, dispatch: AppDispatch): void {
  const product = state.option[tileId];
  if (!product) return;

  const rfsId = getRfsId(product);
  if (rfsId) {
    dispatch(optionApi.endpoints.cancelOptionRfs.initiate({ tileId, rfsId }));
  }

  const { currencyPair, tiering } = product.properties;
  if (currencyPair && tiering) {
    dispatch(espStreamUnsubscribed({ tileId, currencyPair, tiering }));
  }
}

function cleanupOrder(tileId: string, state: AppState, dispatch: AppDispatch): void {
  const product = state.orders[tileId];
  if (!product) return;

  const { currencyPair } = product.properties;
  if (isNotDefined(currencyPair)) {
    return;
  }
  const { currency1, currency2, tiering } = currencyPair;

  if (tiering) {
    dispatch(espStreamUnsubscribed({ tileId, currencyPair: `${currency1}/${currency2}`, tiering }));
  }
}
